// Per-call telemetry for Google Maps API usage, batched then flushed to
// `/api/v2/ui-logs/google-maps`. Never carries raw user queries — only shapes
// (queryLength, resultCount, etc.) so downstream billing forensic aggregation
// stays PII-free. No user identity is recorded either: the BE logs the event
// verbatim (channelGroupId + apiType + metadata) and does NOT attach userEmail,
// so the usage log stays fully PII-free.

export enum GoogleMapsApiType {
  PLACES = 'places',
  AUTOCOMPLETE = 'autocomplete',
  GEOCODING = 'geocoding',
  FEATURE_LAYER = 'featureLayer'
}

type PlacesOperation = 'searchByText' | 'searchNearby' | 'fetchFields';

type PlacesMetadata = {
  operation: PlacesOperation;
  queryLength?: number;
  resultCount: number;
  radius?: number;
  // Session token identity (client-generated, NOT the raw google token object)
  // reconciles autocomplete + subsequent fetchFields into one billable session
  // during daily aggregation.
  sessionId?: string;
  // The requested Place field mask (field NAMES only — no values, so PII-free).
  // The BE derives the billing SKU tier from this against an operation-aware
  // field->SKU table, so we never bake a (frequently-wrong) tier guess on the FE.
  fields?: string[];
};

type AutocompleteMetadata = {
  queryLength: number;
  suggestionCount: number;
  sessionId?: string;
};

type GeocodingMetadata = {
  hasResult: boolean;
};

type FeatureLayerMetadata = {
  featureType: string;
};

type EventBase = {
  channelGroupId: number;
  timestamp: string;
};

export type GoogleMapsUsageEvent =
  | (EventBase & {
      apiType: GoogleMapsApiType.PLACES;
      metadata: PlacesMetadata;
    })
  | (EventBase & {
      apiType: GoogleMapsApiType.AUTOCOMPLETE;
      metadata: AutocompleteMetadata;
    })
  | (EventBase & {
      apiType: GoogleMapsApiType.GEOCODING;
      metadata: GeocodingMetadata;
    })
  | (EventBase & {
      apiType: GoogleMapsApiType.FEATURE_LAYER;
      metadata: FeatureLayerMetadata;
    });

export type GoogleMapsUsageEventInput = Omit<GoogleMapsUsageEvent, 'timestamp'>;
